<template>
  <top-nav />
  <div class="max-w-3xl px-4 mx-auto sm:px-6 lg:px-8">
    <div class="space-y-10 sm:py-6">
      <div class="space-y-10">
        <!-- Upload File -->
        <div>
          <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Upload File</h3>
          <div class="p-4 bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 md:p-6">
            <div class="space-y-2 text-gray-700 dark:text-gray-200">
              <p>1) Select and upload a text based document</p>
              <p>2) Summaries and micro-learnings will be available in your next review</p>
            </div>
            <label for="file-upload" class="relative w-full cursor-pointer">
              <span
                class="inline-flex items-center px-4 py-2 mt-6 text-base font-medium text-white border border-transparent rounded-md shadow-sm bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              >
                <DocumentAddIcon class="w-6 h-6 mr-2 -ml-1" aria-hidden="true" />
                Select File
              </span>

              <input id="file-upload" name="file" ref="file" @change="fileValidation()" type="file" class="sr-only" />
            </label>
            <div class="mt-1 text-xs text-gray-400 dark:text:gray-600 sm:text-sm">
              Valid file formats: <span class="uppercase">{{ validFileFormats.join(", ") }}</span>
            </div>

            <div
              v-if="userFile"
              class="mt-5 rounded-md"
              :class="{
                'text-green-500 dark:text-green-900': validation.format.valid,
                'text-red-500 dark:text-red-900': !validation.format.valid,
              }"
            >
              <div class="text-xl font-bold">
                {{ validation.format.valid ? "Selected file" : "Invalid file format" }}
              </div>
              <div v-if="!validation.format.valid" class="mt-4">
                <p>{{ userFile.type }} is not a valid format</p>
                <p class="mt-3">
                  Supported files: <span class="uppercase">{{ validFileFormats.join(", ") }}</span>
                </p>
              </div>
              <div v-else class="mt-4">
                <div class="grid grid-cols-10 gap-y-2 gap-x-6">
                  <div class="font-semibold">File:</div>
                  <div class="col-span-9 italic">{{ userFile.name }}</div>
                  <div class="font-semibold">Size:</div>
                  <div class="col-span-9">{{ formatBytes(userFile.size) }}</div>
                </div>
                <div class="mt-5">
                  <button
                    v-if="!upload.status"
                    type="button"
                    @click="fileUpload"
                    class="inline-flex items-center px-4 py-2 text-base font-medium text-white bg-green-500 border border-white rounded-md hover:bg-green-400 focus:outline-none dark:hover:bg-green-800"
                  >
                    Upload File
                  </button>
                  <div v-if="upload.progress && upload.progress != 100">{{ upload.progress }}%</div>
                  <div v-if="upload.status === 'complete'" class="flex items-center space-x-2">
                    <CheckCircleIcon class="w-8 h-8 text-white dark:text-green-50" />
                    <span>File uploaded successfully</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit text -->
        <div>
          <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Submit Text</h3>
          <div class="px-4 py-4 overflow-hidden bg-white rounded-lg dark:bg-gray-900 sm:rounded-lg sm:px-6 sm:shadow-lg">
            <Form @submit="textSubmit" class="space-y-4">
              <Field
                type="text"
                name="sourceTitle"
                class="block w-full bg-transparent border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                placeholder="Title"
                rules="required"
              />
              <error-message class="block w-full mt-1 text-sm text-red-500" name="sourceTitle"></error-message>
              <Field name="sourceText" v-slot="{ field }" rules="required|length">
                <textarea
                  v-bind="field"
                  id="textarea"
                  placeholder="Write or paste text"
                  name="sourceText"
                  rows="6"
                  class="block w-full bg-transparent border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:text-white sm:text-sm"
                ></textarea>
              </Field>
              <error-message class="block w-full mt-1 text-sm text-red-500" name="sourceText"></error-message>
              <button
                type="submit"
                class="inline-flex items-center px-4 py-2 mt-6 text-base font-medium text-white border border-transparent rounded-md shadow-sm bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              >
                Submit text
              </button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import TopNav from "../components/nav/TopNav.vue";
import { DocumentAddIcon } from "@heroicons/vue/outline";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import firebaseUser from "../composables/userAuth";
import { ref } from "vue";
import { getStorage, ref as refStorage, uploadBytesResumable } from "firebase/storage";
import { Form, Field, ErrorMessage, defineRule } from "vee-validate";
import { Timestamp, collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "vue-toastification";
export default {
  components: {
    TopNav,
    DocumentAddIcon,
    CheckCircleIcon,
    Form,
    Field,
    ErrorMessage,
  },
  setup() {
    const file = ref(null);
    const { user } = firebaseUser();
    const toast = useToast();
    const userFile = ref(null);
    const validation = ref({
      format: {
        valid: true,
      },
    });
    const upload = ref({
      status: null,
      progress: null,
    });

    const validFileFormats = ["pdf", "doc", "docx", "txt", "dot", "odt", "rtf"];

    const fileValidation = () => {
      // reset to default state (if previously uploaded file was invalid)
      validation.value = {
        format: {
          valid: true,
        },
      };
      upload.value = {
        status: null,
        progress: null,
      };

      userFile.value = file.value.files[0];

      // validate file format
      if (!validFileFormats.some((v) => userFile.value.type.includes(v))) {
        validation.value.format = {
          valid: false,
          message: "Invalid file format",
        };
        return;
      }
    };

    const fileUpload = async () => {
      const storage = getStorage();
      const storageRef = refStorage(storage, `documents/${user.value.uid}/${userFile.value.name}`);
      const metadata = {
        contentType: userFile.value.type,
        customMetadata: {
          uid: user.value.uid,
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, userFile.value, metadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          upload.value.progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);

          switch (snapshot.state) {
            case "paused":
              upload.value.status = "paused";
              console.log("Upload is paused");
              break;
            case "running":
              upload.value.status = "running";
              break;
          }
        },
        (error) => {
          // Handle unsuccessful uploads
          console.log(error);
          upload.value.status = "error";
        },
        () => {
          // Handle successful uploads on complete
          upload.value.status = "complete";
          // getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          //   console.log("File available at", downloadURL);
          // });
        }
      );
    };

    function formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    defineRule("required", (value) => {
      if (!value) {
        return `This field is required`;
      }
      return true;
    });
    defineRule("length", (value) => {
      const numWords = value.split(" ").length;
      if (numWords < 50) {
        return `Please write at least ~20 words. (${numWords} words).`;
      }
      return true;
    });

    const textSubmit = async (e) => {
      const text = e.sourceText.replace(/<\/?[^>]+(>|$)/g, "").replace(/\n/g, "");
      const title = e.sourceTitle.replace(/<\/?[^>]+(>|$)/g, "").replace(/\n/g, "");
      const source = {
        author: null,
        channel: "wisery-web",
        createdAt: Timestamp.now(),
        creator: user.value.uid,
        datePublished: null,
        image: null,
        numTasks: 0,
        publisher: {
          name: null,
          icon: "https://firebasestorage.googleapis.com/v0/b/learn-8c4ef.appspot.com/o/web%2FdocumentIcon.svg?alt=media&token=4de79a24-8d42-4b9e-b495-08fa7393db1a", //documentTextIcon
          url: null,
        },
        text,
        title,
        type: "document",
        url: null,
        users: [user.value.uid],
        wordCount: text.split(" ").length,
      };
      // check if source with same text exists
      let sourceExists = false;
      const sourceCheck = await getDocs(query(collection(db, "sources"), where("users", "array-contains", user.value.uid)));
      sourceCheck.forEach((s) => {
        if (s.data().text === text) {
          sourceExists = true;
        }
      });
      if (!sourceExists) {
        await addDoc(collection(db, "sources"), source);
        toast.success("Text submitted successfully");
      } else {
        return toast.error("Text already submitted");
      }
    };

    return {
      fileUpload,
      file,
      upload,
      validation,
      validFileFormats,
      fileValidation,
      userFile,
      formatBytes,
      textSubmit,
    };
  },
};
</script>
